import React from 'react';

// Import pentagon images
import pentagonAll from '@/assets/digcomp/pentagon-all.png';
import pentagonArea1 from '@/assets/digcomp/pentagon-area-1.png';
import pentagonArea2 from '@/assets/digcomp/pentagon-area-2.png';
import pentagonArea3 from '@/assets/digcomp/pentagon-area-3.png';
import pentagonArea4 from '@/assets/digcomp/pentagon-area-4.png';
import pentagonArea5 from '@/assets/digcomp/pentagon-area-5.png';

// Official DigComp 3.0 area colors (from EU JRC publications)
export const DIGCOMP_AREA_COLORS = {
  1: '#F5C33B', // Area 1: Information & Data Literacy (yellow) - top-right edge
  2: '#3B7DBF', // Area 2: Communication & Collaboration (blue) - right edge
  3: '#F5A623', // Area 3: Digital Content Creation (orange) - bottom edge
  4: '#6AB55D', // Area 4: Safety, Wellbeing & Responsible Use (green) - left edge
  5: '#E8744F', // Area 5: Problem Solving (coral) - top-left edge
};

// Map area numbers to their respective images
const AREA_IMAGES: Record<number | 'all', string> = {
  'all': pentagonAll,
  0: pentagonAll,
  1: pentagonArea1,
  2: pentagonArea2,
  3: pentagonArea3,
  4: pentagonArea4,
  5: pentagonArea5,
};

interface DigcompPentagonIconProps {
  /** The area number (1-5) to highlight the corresponding edge, or 0/"all" for all edges */
  areaNumber: number | 'all';
  /** Color of the highlighted edge (ignored - images have fixed colors) */
  color?: string;
  /** Size in pixels */
  size?: number;
  className?: string;
}

/**
 * DigComp 3.0 Pentagon icon with a colored edge representing one of the 5 competence areas.
 * Each area corresponds to one edge of the pentagon:
 * - Area 1 (Information): Top-right edge (yellow)
 * - Area 2 (Communication): Right edge (blue)
 * - Area 3 (Content Creation): Bottom edge (orange)
 * - Area 4 (Safety): Left edge (green)
 * - Area 5 (Problem Solving): Top-left edge (coral)
 * 
 * When areaNumber is 'all' or 0, all edges are shown with their respective colors.
 */
export function DigcompPentagonIcon({ 
  areaNumber, 
  size = 48,
  className 
}: DigcompPentagonIconProps) {
  const imageKey = areaNumber === 'all' ? 'all' : areaNumber;
  const imageSrc = AREA_IMAGES[imageKey] || pentagonAll;
  
  return (
    <img 
      src={imageSrc}
      alt={`DigComp Area ${areaNumber === 'all' ? 'All' : areaNumber}`}
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      style={{ objectFit: 'contain' }}
    />
  );
}
